import React, { useMemo } from 'react'
import { useTable, useExpanded, type Row } from 'react-table'
import './organizations-table.css'
import { Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import { useSessionContext } from 'session'

export interface Organization {
  id: number
  name: string
  country: string
  type: string
  top: string
  parent: string
}

interface OrgTableProps {
  organizations: Organization[]
  orderField: string
  orderDirection: string
  updateOrdering: (field: string, direction: string) => void
}

export const OrganizationsTable: React.FC<OrgTableProps> = ({ organizations, orderField, orderDirection, updateOrdering }) => {
  const navigate = useNavigate()
  const { sessionData, openNewTab } = useSessionContext()

  const columns: any = useMemo(
    () => [
      { Header: 'ID', accessor: 'id', order_field: 'id' },
      { Header: 'Unique ID', accessor: 'uniqueId', order_field: 'uniqueid' },
      { Header: 'Name', accessor: 'organization_name', order_field: 'name' },
      { Header: 'Country', accessor: 'country.country_desc', order_field: 'country' },
      { Header: 'Type', accessor: 'type.type_desc_short', order_field: 'type' },
      { Header: 'Top Org.', accessor: 'organization_top_level.organization_name', order_field: 'top_org' },
      {
        Header: 'Parent Org.',
        accessor: (row: any) => `${row.organization_parent.id as number}: ${row.organization_parent.organization_name as string}`,
        order_field: 'parent_org'
      }, {
        Header: 'Edit',
        id: 'edit',
        Cell: ({ row }: { row: Row<Organization> }) => (
                    <Button
                        variant="contained"
                        size='medium'
                        onClick={() => {
                          openNewTab(`/editOrganization/${row.values.id as number}`)
                        }}
                    >
                        <ModeEditIcon />
                    </Button>
        )
      }
    ],
    [navigate, sessionData.newTab]
  )

  const tableData = useMemo(() => organizations, [organizations])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable<Organization>(
    {
      columns,
      data: tableData
    },
    useExpanded
  )

  function handleHeaderClick (column: any): void {
    const direction = orderField === column.order_field ? (orderDirection === 'ASC' ? 'DESC' : 'ASC') : orderDirection
    updateOrdering(column.order_field, direction)
  }

  return (
      <React.Fragment>
          {organizations.length === 0
            ? (
              <p>No organizations to show.</p>
              )
            : (
        <table {...getTableProps()} className="table">
            <thead>
            {headerGroups.map((headerGroup) => {
              return (
                    <tr {...headerGroup.getHeaderGroupProps} id = {'row' + headerGroup.id} key={'header-row-' + headerGroup.id}>
                        {headerGroup.headers.map((column: any) => (
                            <th
                                {...column.getHeaderProps}
                                key={column.id}
                                onClick={() => { if (column.id !== 'edit') handleHeaderClick(column) }}
                                className={column.id === 'edit' ? 'noclick-column-header' : ''}
                            >
                                <div style={{ display: 'flex', flexDirection: 'row' }}>
                                {column.render('Header')}
                                <span>
                                    {orderField === column.order_field ? (orderDirection === 'ASC' ? ' ↓' : ' ↑') : ''}
                                </span>
                                </div>
                            </th>))}
                    </tr>
              )
            })}
            </thead>
            <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row)
              return (
                    <tr {...row.getRowProps()} key={'row-' + row.id}>
                        {row.cells.map((cell) => (
                            <td {...cell.getCellProps()} key={'cell -' + cell.column.id}>{cell.render('Cell')}</td>
                        ))}
                    </tr>
              )
            })}
            </tbody>
        </table>
              )}
      </React.Fragment>
  )
}
